import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, Eye, EyeOff } from 'lucide-react';
import Layout from '../../components/Layout';
import { userService, RoleDTO } from '../../services/userService';
import { outletService } from '../../services/outletService';
import { useModule } from '../../context/ModuleContext';
import { APP_LABELS } from '../../context/AppAccessContext';
import { rolesRequireOutlet } from '../../utils/outletRequirement';
import '../../styles/ImsTheme.css';
import '../../pages/Products/ProductCategoryCreatePage.css';

import { getServerErrorMessage } from '../../utils/errorUtils';

interface OutletOption {
  id: number;
  name: string;
}

interface CreateUserFormData {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  roleIds: number[];
  outletId: number | null;
}

const UserCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { company } = useModule();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roles, setRoles] = useState<RoleDTO[]>([]);
  const [outlets, setOutlets] = useState<OutletOption[]>([]);
  const [companyApps, setCompanyApps] = useState<string[]>([]);
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState<CreateUserFormData>({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    roleIds: [],
    outletId: null,
  });

  // A cashier/waiter/kitchen role binds the user to an outlet, so the picker
  // becomes required. Backed by the role's permissions, matching the server rule.
  const outletRequired = rolesRequireOutlet(roles, formData.roleIds);

  useEffect(() => {
    loadRoles();
    loadOutlets();
    if (company?.id) loadCompanyApps(company.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);

  const loadRoles = async () => {
    try {
      const response = await userService.getRoles();
      const data = response?.data?.data;
      if (Array.isArray(data)) setRoles(data);
    } catch {
      // non-critical
    }
  };

  const loadOutlets = async () => {
    try {
      const response = await outletService.getAll(1, 100);
      const data = (response?.data as any)?.data?.data ?? (response?.data as any)?.data;
      if (Array.isArray(data)) setOutlets(data);
    } catch {
      // non-critical
    }
  };

  const loadCompanyApps = async (companyId: number) => {
    try {
      const response = await userService.getCompanyApps(companyId);
      const data = response?.data?.data;
      if (Array.isArray(data)) setCompanyApps(data);
    } catch {
      // non-critical
    }
  };

  const handleAppToggle = (appCode: string) => {
    setSelectedApps((prev) =>
      prev.includes(appCode) ? prev.filter((a) => a !== appCode) : [...prev, appCode]
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRoleToggle = (roleId: number) => {
    setFormData((prev) => ({
      ...prev,
      roleIds: prev.roleIds.includes(roleId)
        ? prev.roleIds.filter((id) => id !== roleId)
        : [...prev.roleIds, roleId],
    }));
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    if (!formData.email.trim()) {
      setError(t('user.formValidation.emailRequired'));
      return false;
    }
    if (!validateEmail(formData.email)) {
      setError(t('user.formValidation.emailInvalid'));
      return false;
    }
    if (!formData.firstName.trim()) {
      setError(t('user.formValidation.firstNameRequired'));
      return false;
    }
    if (!formData.lastName.trim()) {
      setError(t('user.formValidation.lastNameRequired'));
      return false;
    }
    if (!formData.password) {
      setError(t('user.formValidation.passwordRequired'));
      return false;
    }
    if (formData.password.length < 8) {
      setError(t('user.formValidation.passwordTooShort'));
      return false;
    }
    if (!formData.confirmPassword) {
      setError(t('user.formValidation.confirmPasswordRequired'));
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(t('user.formValidation.passwordsDoNotMatch'));
      return false;
    }
    if (outletRequired && !formData.outletId) {
      setError(t('user.formValidation.outletRequired'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!validateForm()) return;

    setSaving(true);
    try {
      const created = await userService.createUser({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        password: formData.password,
        roleIds: formData.roleIds,
        outletId: formData.outletId,
      });
      const newUserId = created?.data?.data?.id;
      if (selectedApps.length > 0) {
        // Never drop the app selection silently — without app rows the user
        // logs in to an empty app switcher with no indication why.
        if (!newUserId) throw new Error(t('user.appAssignmentFailed'));
        await userService.setUserApps(newUserId, selectedApps);
      }
      navigate('/users', { state: { message: t('user.userCreatedSuccess') } });
    } catch (err: any) {
      setError(getServerErrorMessage(err) || t('user.failedToCreate'));
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="create-container">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/users')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('user.createTitle')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/users')} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={saving}>
              {saving ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('user.createTitle')}</>}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="create-content">
          <div className="create-main-section">
            <div className="create-card">
              <div className="create-card-header">
                <h2 className="create-card-title">{t('user.accountInfo')}</h2>
              </div>
              <div className="create-card-body">
                <form id="page-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label className="form-label">
                      {t('user.email')} <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      className="form-input"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      autoComplete="email"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('user.firstName')} <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('user.lastName')} <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('user.password')} <span className="required">*</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="form-input"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        autoComplete="new-password"
                        required
                        style={{ paddingInlineEnd: '40px' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        style={{
                          position: 'absolute',
                          insetInlineEnd: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#6b7280',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        title={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <EyeOff size={18} strokeWidth={2} />
                        ) : (
                          <Eye size={18} strokeWidth={2} />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('user.confirmPassword')} <span className="required">*</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        className="form-input"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        autoComplete="new-password"
                        required
                        style={{ paddingInlineEnd: '40px' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        style={{
                          position: 'absolute',
                          insetInlineEnd: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#6b7280',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        title={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? (
                          <EyeOff size={18} strokeWidth={2} />
                        ) : (
                          <Eye size={18} strokeWidth={2} />
                        )}
                      </button>
                    </div>
                  </div>

                  {companyApps.length > 0 && (
                    <div className="form-group">
                      <label className="form-label">{t('userModules.title')}</label>
                      <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', marginTop: '2px' }}>
                        {t('userModules.subtitle')}
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {companyApps.map((appCode) => (
                          <label
                            key={appCode}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedApps.includes(appCode)}
                              onChange={() => handleAppToggle(appCode)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                            <span style={{ fontWeight: 500 }}>{APP_LABELS[appCode] ?? appCode}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {outlets.length > 0 && (
                    <div className="form-group">
                      <label className="form-label">
                        {t('user.outlet')}
                        {outletRequired && <span className="required"> *</span>}
                      </label>
                      <select
                        className="form-select"
                        value={formData.outletId ?? ''}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            outletId: e.target.value ? parseInt(e.target.value) : null,
                          }))
                        }
                      >
                        <option value="">{t('user.outletNotAssigned')}</option>
                        {outlets.map((o) => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </select>
                      <small className="form-helper">{t('user.outletHelper')}</small>
                    </div>
                  )}

                  {roles.length > 0 && (
                    <div className="form-group">
                      <label className="form-label">{t('user.roles')}</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                        {roles.map((role) => (
                          <label
                            key={role.id}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}
                          >
                            <input
                              type="checkbox"
                              checked={formData.roleIds.includes(role.id)}
                              onChange={() => handleRoleToggle(role.id)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                            <span style={{ fontWeight: 500 }}>{role.name}</span>
                            {role.description && (
                              <span style={{ color: '#6b7280', fontSize: '12px' }}>— {role.description}</span>
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                </form>
              </div>
            </div>
          </div>

          <div className="create-sidebar">
            <div className="create-card">
              <div className="create-card-header">
                <h3 className="create-card-title">{t('user.requiredFields')}</h3>
              </div>
              <div className="create-card-body">
                <ul style={{ paddingInlineStart: '16px', margin: 0, fontSize: '13px', lineHeight: '1.8' }}>
                  <li>{t('user.email')}</li>
                  <li>{t('user.firstName')}</li>
                  <li>{t('user.lastName')}</li>
                  <li>{t('user.password')}</li>
                  <li>{t('user.confirmPassword')}</li>
                </ul>
              </div>
            </div>

            <div className="create-card">
              <div className="create-card-header">
                <h3 className="create-card-title">{t('user.passwordRules')}</h3>
              </div>
              <div className="create-card-body">
                <ul style={{ paddingInlineStart: '16px', margin: 0, fontSize: '13px', lineHeight: '1.8' }}>
                  <li>{t('user.passwordMinLength')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UserCreatePage;
